// 학생 주간 스케줄 페이지 (시간 기반 입력)
// ⭐ 2026-07-02: 교시 체크 그리드 → 요일별 시간 입력으로 전환.
//   관리자가 시간(예: 9:00)으로 주입해도 교시로 투영돼 1교시(8:10)로 보이던 문제 해결.
//   백엔드(PUT /period-schedules)는 이미 시간범위 [{start,end}] 저장을 지원함(시간=진실).

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Switch,
  TextField,
  Stack,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useStudentAuth } from '../hooks/useStudentAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const DEFAULT_START = '09:00';
const DEFAULT_END = '22:00';

interface DayTime {
  enabled: boolean;
  start: string;
  end: string;
}
type DayMap = Record<string, DayTime>;

const buildEmpty = (): DayMap => {
  const m: DayMap = {};
  for (let d = 0; d < 7; d++) m[String(d)] = { enabled: false, start: DEFAULT_START, end: DEFAULT_END };
  return m;
};

export default function PeriodSchedulePage() {
  const { student } = useStudentAuth();
  const [dayTimes, setDayTimes] = useState<DayMap>(buildEmpty());
  const [savedDayTimes, setSavedDayTimes] = useState<DayMap>(buildEmpty());
  // 시간=진실: 학생이 안 건드린 요일은 관리자 원본(멀티범위 포함)을 그대로 보존해 정밀도 훼손 방지
  const [rawByDay, setRawByDay] = useState<Record<string, any>>({});
  const [multiRangeDays, setMultiRangeDays] = useState<Record<string, boolean>>({});
  const [modifierInfo, setModifierInfo] = useState<{ modifier_role: string; updated_at: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (student?.id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  const fetchData = async () => {
    if (!student?.id) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('student_access_token');
      const res = await fetch(`${API_BASE_URL}/api/v1/student-schedules/${student.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // day_ranges: 요일별 실제 시간 범위 [{start,end}] (관리자/학부모/학생이 넣은 진짜 시간)
        const dr: Record<string, { start: string; end: string }[]> = data.day_ranges || {};
        const next = buildEmpty();
        const multi: Record<string, boolean> = {};
        for (let d = 0; d < 7; d++) {
          const key = String(d);
          const ranges = dr[key] || [];
          if (ranges.length > 0) {
            next[key] = {
              enabled: true,
              start: (ranges[0].start || DEFAULT_START).slice(0, 5),
              end: (ranges[ranges.length - 1].end || DEFAULT_END).slice(0, 5),
            };
            if (ranges.length > 1) multi[key] = true;
          }
        }
        setDayTimes(next);
        setSavedDayTimes(JSON.parse(JSON.stringify(next)));
        setMultiRangeDays(multi);
        setRawByDay(data.schedule_raw || {});
        setModifierInfo(data.modifier_info || null);
      }
    } catch (err) {
      console.error('스케줄 조회 에러:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateDay = (key: string, patch: Partial<DayTime>) =>
    setDayTimes((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const hasChanges = JSON.stringify(dayTimes) !== JSON.stringify(savedDayTimes);

  const handleSave = async () => {
    if (!student?.id) return;

    // 검증: 켜진 요일은 종료 > 시작
    for (let d = 0; d < 7; d++) {
      const dt = dayTimes[String(d)];
      if (dt.enabled && dt.start >= dt.end) {
        setError(`${DAYS[d]}요일: 종료 시각이 시작 시각보다 늦어야 합니다.`);
        return;
      }
    }

    // 빈 저장 방어 — 모든 요일 휴무면 기존 스케줄 전부 삭제됨
    const anyEnabled = Object.values(dayTimes).some((d) => d.enabled);
    if (!anyEnabled) {
      const ok = window.confirm(
        '모든 요일이 휴무입니다.\n이대로 저장하면 기존 주간 스케줄이 전부 삭제됩니다.\n\n계속하시겠습니까?'
      );
      if (!ok) return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    // 안 건드린 요일 → 원본(rawByDay) 보존, 건드린 요일 → 새 시간범위
    const payload: Record<string, any> = {};
    for (let d = 0; d < 7; d++) {
      const key = String(d);
      const cur = dayTimes[key];
      const saved = savedDayTimes[key];
      const changed = JSON.stringify(cur) !== JSON.stringify(saved);
      if (!changed && rawByDay[key] !== undefined) {
        payload[key] = rawByDay[key];
      } else if (cur.enabled) {
        payload[key] = [{ start: cur.start, end: cur.end }];
      } else {
        payload[key] = [];
      }
    }

    try {
      const token = localStorage.getItem('student_access_token');
      const res = await fetch(`${API_BASE_URL}/api/v1/student-schedules/${student.id}/period-schedules`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedules: payload }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || '저장 실패');
      }
      setSavedDayTimes(JSON.parse(JSON.stringify(dayTimes)));
      setModifierInfo({ modifier_role: 'student', updated_at: new Date().toISOString() });
      setSuccess('주간 스케줄이 저장되었습니다.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const ROLE_LABEL: Record<string, string> = { parent: '학부모', student: '학생', owner: '관리자' };
  const formatModifiedAt = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch {
      return iso;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CalendarMonthIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          주간 스케줄
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        요일별 입실~퇴실 시간을 설정하세요. 시간은 10분 단위로 입력할 수 있습니다.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack divider={<Divider flexItem />} spacing={1}>
          {DAYS.map((day, d) => {
            const key = String(d);
            const dt = dayTimes[key];
            return (
              <Box
                key={key}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 92 }}>
                  <Switch
                    checked={dt.enabled}
                    onChange={(e) => updateDay(key, { enabled: e.target.checked })}
                    size="small"
                  />
                  <Typography fontWeight={600} sx={{ width: 22 }}>
                    {day}
                  </Typography>
                </Box>
                {dt.enabled ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                      type="time"
                      size="small"
                      value={dt.start}
                      onChange={(e) => updateDay(key, { start: e.target.value })}
                      inputProps={{ step: 600 }}
                      sx={{ width: 128 }}
                    />
                    <Typography color="text.secondary">~</Typography>
                    <TextField
                      type="time"
                      size="small"
                      value={dt.end}
                      onChange={(e) => updateDay(key, { end: e.target.value })}
                      inputProps={{ step: 600 }}
                      sx={{ width: 128 }}
                    />
                    {multiRangeDays[key] && (
                      <Typography variant="caption" color="warning.main">
                        여러 시간대 → 수정 시 이 범위로 합쳐집니다
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    휴무
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Paper>

      {modifierInfo && modifierInfo.updated_at && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          마지막 수정: {ROLE_LABEL[modifierInfo.modifier_role] || modifierInfo.modifier_role} |{' '}
          {formatModifiedAt(modifierInfo.updated_at)}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          size="large"
        >
          {isSaving ? '저장 중...' : '스케줄 저장'}
        </Button>
      </Box>
    </Box>
  );
}
